"use client";

import { useEffect } from "react";
import Script from "next/script";
import { useConsent } from "@forestsync/ui";

declare global {
  interface Window {
    gtag?: (...args: [string, ...unknown[]]) => void;
    clarity?: (...args: [string, ...unknown[]]) => void;
    dataLayer?: unknown[];
  }
}

const GA4_RE = /^G-[A-Z0-9]+$/;
const CLARITY_RE = /^[a-z0-9]+$/i;

const rawGa4 = process.env.NEXT_PUBLIC_REGISTRY_GA4_ID ?? "";
const rawClarity = process.env.NEXT_PUBLIC_REGISTRY_CLARITY_ID ?? "";

const GA4_ID = GA4_RE.test(rawGa4) ? rawGa4 : null;
const CLARITY_ID = CLARITY_RE.test(rawClarity) ? rawClarity : null;

const CONSENT_KEY = "registry_cookie_consent";

export function AnalyticsScripts() {
  const consent = useConsent(CONSENT_KEY);

  // Update GA4 consent mode once user's preference is known
  useEffect(() => {
    if (consent === undefined) return;
    if (typeof window.gtag !== "function") return;
    if (consent?.analytics) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
      });
    } else {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
      });
    }
  }, [consent]);

  return (
    <>
      {/* GA4 — always loads with consent defaults denied; updated via useEffect above */}
      {GA4_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', wait_for_update: 2000 });
              gtag('js', new Date());
              gtag('config', '${GA4_ID}');
            `}
          </Script>
        </>
      )}

      {/* Microsoft Clarity — only loaded after explicit analytics consent */}
      {CLARITY_ID && consent?.analytics && (
        <Script id="clarity-init" strategy="lazyOnload">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window,document,"clarity","script","${CLARITY_ID}");
          `}
        </Script>
      )}
    </>
  );
}
